/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Component imports
import { Avatar, Row, Typography, notification } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import AppLogo from '../assets/logo.svg';

// Misc imports
import store from '../store';
import { NAV_LIVECHAT } from '../constants/navigation';
import { CONVERSATION_STATUSES, INCOMING_EVENTS, LIVECHAT_SCREEN_TABS, REQUEST_STATUSES } from '../constants/liveChat';
import { LIVECHAT_ERROR_CODES } from '../constants/errors';
import { livechatActions } from '../store/liveChat/liveChatSlice';
import { readConversation } from '../store/liveChat/liveChatThunks';
import replaceUrlParams from '../utils/string/replaceUrlParams';
import { IS_LIVECHAT_REMINDER_ENABLED } from '../constants/env';
import satisfiesInboxFilters from '../utils/misc/satisfiesInboxFilters';
import { NOTIFICATIONS_PERMISSIONS } from '../constants/notifications';
import { apiSlice } from '../store/apiSlice';
import { addConversation, addConversationMessage, removeConversation, setConversationRead, updateConversationOnMessage, updateConversationStatus, updateTransferredBackConversation } from '../store/liveChat/liveChatActions';
import openConsoleAlertMessage from '../utils/misc/openConsoleAlertMessage';
import i18n from 'i18next';

/**
 * Handler for the socket actions.
 * Each method represents a specific action available for the socket. We import the "enum" from the constants file
 * We can also communicate with the store (i.e. dispatch) as we have imported the store already.
 */

export default class actionHandler {
  constructor() {
    this.notificationsMap = new Map();
    this.latestRequestId = '';
    this.latestIdleId = '';
  }

  [INCOMING_EVENTS.AUTHORIZATION_GRANTED]() {
    store.dispatch(livechatActions.authorizeConnectionComplete());
  }

  async [INCOMING_EVENTS.REQUEST_BEGIN](request, navigate) {
    const location = store.getState().router.location;
    const orgId = store.getState().auth.organizationId;
    if (location.pathname.includes(replaceUrlParams(NAV_LIVECHAT, { orgId }))) {
      // If the state exists, update it, otherwise, upsert it
      // This is because updateQueryData doesn't work if the query doesn't exist
      const cachedResult = apiSlice.endpoints.fetchRequests.select({ orgId })(store.getState());
      if (cachedResult?.data) {
        store.dispatch(apiSlice.util.updateQueryData('fetchRequests', { orgId }, (draft) => {
          draft.data = [request, ...draft.data];
          draft.total = draft.total + 1;
        }));
      } else {
        store.dispatch(apiSlice.util.upsertQueryData('fetchRequests', { orgId }, { data: [request] }));
      }
    }

    this.createNotification(request, false, navigate);

    const sound = document.getElementById('hbfNotificationSound');
    if (sound && store.getState().livechat.requestsSoundEnabled) {
      sound.play();
    }
  }

  async [INCOMING_EVENTS.REQUEST_CREATE_ACK](conversation) {
    const orgId = store.getState().auth.organizationId;
    const activeConversationId = store.getState().livechat?.activeConversationInfo?.id;
    if (activeConversationId === conversation.id) {
      await store.dispatch(apiSlice.endpoints.fetchActiveConversation.initiate({ orgId, conversationId: activeConversationId }));
      store.dispatch(livechatActions.transferConversationComplete());
      openConsoleAlertMessage({ type: 'success', message: i18n.t('Invite sent.') });
    }
    updateConversationStatus();
  }

  [INCOMING_EVENTS.REQUEST_END](request) {
    this.updateRequest(request);
  }

  [INCOMING_EVENTS.REQUEST_MISSED](request) {
    this.updateRequest(request);
  }

  [INCOMING_EVENTS.CONVERSATION_CREATE](conversation, navigate, metadata) {
    if (satisfiesInboxFilters(conversation, true)) {
      addConversation(conversation);
    }
    this.navigateToConversation(navigate, metadata?.agentId, conversation);
  }

  [INCOMING_EVENTS.CONVERSATION_UPDATE](conversation, navigate, metadata) {
    const orgId = store.getState().auth.organizationId;
    const activeConversation = store.getState().livechat?.activeConversationInfo;
    const activeConversationId = activeConversation?.id;
    const cachedResult = apiSlice.endpoints.fetchConversations.select({ orgId })(store.getState());
    const conversations = cachedResult?.data?.data || [];

    if (activeConversationId === conversation.id) {
      if (metadata?.join === false) {
        updateTransferredBackConversation(conversation);
      } else {
        if (!activeConversationId) {
          return;
        }

        store.dispatch(apiSlice.util.updateQueryData(`fetchActiveConversation`, { orgId, conversationId: activeConversationId }, (draft) => {
          return {
            ...conversation
          };
        }));
      }
    }

    if (satisfiesInboxFilters(conversation)) {
      if (conversations.find(currentConversation => currentConversation.id === conversation.id)) {
        updateConversationStatus(conversation);
      } else {
        addConversation(conversation);
      }
    } else {
      removeConversation(conversation);
    }

    // If the conversation was terminated, we should remove it from the list
    if (activeConversation?.status === CONVERSATION_STATUSES.STARTED && conversation.status === CONVERSATION_STATUSES.FINISHED) {
      removeConversation(conversation);
      const location = store.getState().router.location;
      const urlSearchParams = new URLSearchParams(location.search);
      // Remove also terminated conversation content from messenger view
      if (urlSearchParams.get('conversation') === conversation.id.toString()) {
        navigate({
          pathname: location.pathname,
          search: `tab=${LIVECHAT_SCREEN_TABS.OPEN}`
        });
        return;
      }
    }

    this.navigateToConversation(navigate, metadata?.agentId, conversation);
  }

  [INCOMING_EVENTS.CONVERSATION_READ_ACK](conversation) {
    const currentUserId = store.getState().auth.currentUser?.id;
    if (currentUserId) {
      setConversationRead(conversation, currentUserId);
    }
  }

  [INCOMING_EVENTS.MESSAGE_SENT](message) {
    const activeConversationId = store.getState().livechat?.activeConversationInfo?.id;

    if (message.livechatConversation.id === activeConversationId) {
      store.dispatch(readConversation(message.livechatConversation.id));
    }

    addConversationMessage(message);

    if (satisfiesInboxFilters(message.livechatConversation)) {
      updateConversationOnMessage(message);
    } else {
      removeConversation(message.livechatConversation);
    }
  }

  [INCOMING_EVENTS.NOTIFICATION](reminder, navigate) {
    if (IS_LIVECHAT_REMINDER_ENABLED) {
      this.createNotification(reminder.entity, true, navigate);
    }
  }

  [INCOMING_EVENTS.ERROR](error) {
    openConsoleAlertMessage({ type: 'error', message: LIVECHAT_ERROR_CODES[error?.error ?? 'LCM_000'] });
  }

  createNotification(entity, reminder = false, navigate) {
    const location = store.getState().router.location;

    const notificationId = `${reminder ? 'conversation_' : 'request_'}${entity.id}`;
    const notificationTitle = reminder ? 'Conversation Inactivity' : 'New request';
    const notificationText = reminder
      ? i18n.t('Conversation {{id}} has been idle for too long', { id: entity?.id })
      : i18n.t('You have a new LiveChat request from {{platformUserName}}', { platformUserName: entity.platformUserName });

    const onNotificationClick = () => {
      let redirectPathname;
      if (location.pathname.includes(replaceUrlParams(NAV_LIVECHAT, { orgId: store.getState().auth.organizationId }))) {
        redirectPathname = location.pathname;
      } else {
        redirectPathname = replaceUrlParams(NAV_LIVECHAT, { orgId: store.getState().auth.organizationId });
      }
      if (entity.organizationId === store.getState().auth.organizationId) {
        const urlSearchParams = new URLSearchParams(location.search);
        const redirectSearch = reminder ? `tab=${LIVECHAT_SCREEN_TABS.OPEN}&conversation=${entity.id}` : `tab=${LIVECHAT_SCREEN_TABS.NEW}&conversation=${urlSearchParams.get('conversation')}`;
        navigate({
          pathname: redirectPathname,
          search: redirectSearch
        }, { state: !reminder ? { request: entity } : {} });
      }

      window.focus();
      pushNotification?.close();
      this.notificationsMap.delete(notificationId);

      if (reminder) {
        const latest = this.latestIdleId === entity.id;
        return latest && notification.close('reminder');
      } else {
        const latest = this.latestRequestId === entity.id;
        return latest && notification.close('request');
      }
    };

    notification.open({
      className: 'livechat-notification',
      key: reminder ? 'reminder' : 'request',
      placement: 'bottomRight',
      message:
        <Row wrap={false} align="middle">
          <Avatar.Group className="mr-2">
            <Avatar src={entity.tenantImageUrl} />
            <Avatar icon={<UserOutlined/>}/>
          </Avatar.Group>
          <Typography.Text className="fs-lg" ellipsis>{notificationTitle}</Typography.Text>
        </Row>,
      description: notificationText,
      onClick: onNotificationClick
    });

    let pushNotification;
    if (store.getState().livechat.pushNotificationsPermission === NOTIFICATIONS_PERMISSIONS.GRANTED) {
      pushNotification = new Notification(notificationTitle, {
        body: notificationText,
        icon: AppLogo,
        image: entity.tenantImageUrl,
        badge: AppLogo,
        requireInteraction: true,
        tag: notificationId
      });
      pushNotification.addEventListener('click', onNotificationClick);

      this.notificationsMap.set(notificationId, pushNotification);
    }

    if (reminder) {
      this.latestIdleId = entity.id;
    } else {
      this.latestRequestId = entity.id;
    }
  }

  updateRequest(request) {
    const orgId = store.getState().auth.organizationId;
    const { data, originalArgs } = apiSlice.endpoints.fetchRequests.select({ orgId })(store.getState());
    const existingRequests = data?.data || [];
    const updateIndex = existingRequests.findIndex(r => r.id === request.id);
    // if cached list exists and is for new requests, remove the updated request, else update list
    if (originalArgs?.urlParams?.status === REQUEST_STATUSES.NEW && updateIndex !== -1) {
      store.dispatch(apiSlice.util.updateQueryData('fetchRequests', { orgId }, (draft) => {
        draft.data.splice(updateIndex, 1);
        draft.total = draft.total - 1;
      }));
    } else {
      if (updateIndex !== -1) {
        store.dispatch(apiSlice.util.updateQueryData('fetchRequests', { orgId }, (draft) => {
          draft.data[updateIndex] = {
            ...draft.data[updateIndex],
            ...request
          };
        }));
      }
    }

    const pushNotification = this.notificationsMap.get(`request_${request.id}`);

    if (pushNotification) {
      pushNotification.close();
    }
    if (this.latestRequestId === request.id) {
      notification.close('request');
    }
  }

  navigateToConversation(navigate, agentId, conversation) {
    const location = store.getState().router.location;
    const currentUserId = store.getState().auth.currentUser?.id;
    const orgId = store.getState().auth.organizationId;
    const currentLocation = location.pathname;

    if (currentUserId === agentId) {
      if (currentLocation === replaceUrlParams(NAV_LIVECHAT, { orgId })) {
        navigate({
          pathname: location.pathname,
          search: `tab=${LIVECHAT_SCREEN_TABS.OPEN}&conversation=${conversation.id}`
        }, { replace: false });
      }
    }
  }
}
